import { Marked } from 'marked';

/** Strip whitespace and any quote wrapper the model may add around the edit. */
export const normalizeInlineEditResult = (raw: string): string => {
  let text = raw.trim();
  const pairs: Array<[string, string]> = [
    ['"', '"'],
    ['“', '”'],
  ];
  for (const [open, close] of pairs) {
    if (text.startsWith(open) && text.endsWith(close) && text.length >= 2) {
      text = text.slice(open.length, -close.length).trim();
    }
  }
  return text;
};

const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const asLiteral = ({ raw }: { raw: string }): string => escapeHtml(raw);

const asLiteralBlock = ({ raw }: { raw: string }): string =>
  `<p>${escapeHtml(raw.trim())}</p>`;

/**
 * The inline edit runs with `content_type: MARKDOWN`, so the result is
 * markdown. Only bold, italic and (when the editor has list nodes) lists are
 * converted; raw HTML, links, inline code, strikethrough, task checkboxes and
 * the other block syntax stay literal text because the note schema has no
 * nodes or marks for them and would drop the URL or markers.
 */
const inlineEditMarked = new Marked({
  gfm: true,
  renderer: {
    html: ({ text }) => escapeHtml(text),
    link: asLiteral,
    image: asLiteral,
    codespan: asLiteral,
    del: asLiteral,
    checkbox: ({ checked }) => (checked ? '[x]' : '[ ]'),
    heading: asLiteralBlock,
    blockquote: asLiteralBlock,
    table: asLiteralBlock,
    code: asLiteralBlock,
  },
});

export type InsertableHtmlOptions = {
  /** The target editor's schema has bullet and ordered list nodes. */
  lists?: boolean;
};

const renderLine = (line: string): string =>
  inlineEditMarked.parseInline(line, { async: false });

const renderParagraphs = (text: string): string[] =>
  text
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.replace(/^\n+|\n+$/g, ''))
    .filter(Boolean)
    .map((paragraph) => paragraph.split('\n').map(renderLine).join('<br>'));

/**
 * List blocks become `<ul>`/`<ol>`; every other run of blocks goes through
 * the literal paragraph path. `null` when the text has no list.
 */
const renderWithLists = (text: string): string | null => {
  const tokens = inlineEditMarked.lexer(text);
  if (!tokens.some((token) => token.type === 'list')) return null;
  const blocks: string[] = [];
  let pending = '';
  const flushPending = () => {
    blocks.push(...renderParagraphs(pending).map((paragraph) => `<p>${paragraph}</p>`));
    pending = '';
  };
  for (const token of tokens) {
    if (token.type !== 'list') {
      pending += token.raw;
      continue;
    }
    flushPending();
    blocks.push(inlineEditMarked.parser([token]).replace(/>\n/g, '>').trim());
  }
  flushPending();
  return blocks.join('');
};

/**
 * Markdown → insertable HTML. Lists convert only when `options.lists` says
 * the editor can hold them; other block syntax (headings, quotes, tables)
 * stays literal text because the note schema has no node for it. Single
 * paragraphs stay inline (no `<p>` wrapper) so short rewrites don't split
 * the surrounding block; multi-line results become paragraphs with hard
 * breaks.
 */
export const toInsertableHtml = (
  text: string,
  options: InsertableHtmlOptions = {}
): string => {
  const withLists = options.lists ? renderWithLists(text) : null;
  if (withLists !== null) return withLists;
  const paragraphs = renderParagraphs(text);
  if (paragraphs.length <= 1) return paragraphs[0] ?? '';
  return paragraphs.map((paragraph) => `<p>${paragraph}</p>`).join('');
};

/**
 * Like `toInsertableHtml` but always block-level — used by "Insert below"
 * so the new text lands on its own line(s) beneath the original.
 */
export const toInsertableBlockHtml = (
  text: string,
  options: InsertableHtmlOptions = {}
): string => {
  const html = toInsertableHtml(text, options);
  if (!html) return '';
  return /^<(p|ul|ol)\b/.test(html) ? html : `<p>${html}</p>`;
};
